import { format } from 'date-fns';

import { PlannerMessage, ParsedMessageData } from './types';
import {
  LEAVE_REQUEST_TASK_KEYS,
  DUTY_CHANGE_TASK_KEYS,
  FLEX_NOTIFICATION_TASK_KEYS
} from './constants';

type TranslateFn = (key: string, options?: { defaultValue?: string }) => string;

const CREATED_AT_FORMAT = 'd MMM yyyy (H:mm)';

const formatCreatedAt = (createTime: string): string =>
  format(new Date(createTime), CREATED_AT_FORMAT);

const TASK_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  LeaveRequestCreateTask: 'plannerMessages.taskTypes.leaveRequestCreateTask',
  LeaveRequestSeenTask: 'plannerMessages.taskTypes.leaveRequestSeenTask',
  LeaveRequestAfterSeenTask:
    'plannerMessages.taskTypes.leaveRequestAfterSeenTask',
  LeaveRequestProcessedTask:
    'plannerMessages.taskTypes.leaveRequestProcessedTask',
  AgendaDutyLineCreateManagerApprove:
    'plannerMessages.taskTypes.agendaDutyLineCreateManagerApprove',
  AgendaDutyLineEditManagerApprove:
    'plannerMessages.taskTypes.agendaDutyLineEditManagerApprove',
  RosterDutyLineEditManagerApprove:
    'plannerMessages.taskTypes.rosterDutyLineEditManagerApprove',
  RosterDutyLineCreateManagerApprove:
    'plannerMessages.taskTypes.rosterDutyLineCreateManagerApprove',
  RosterDutyLineEditNotifyPlannerRejected:
    'plannerMessages.taskTypes.rosterDutyLineEditNotifyPlannerRejected',
  RosterDutyLineCreateNotifyPlannerRejected:
    'plannerMessages.taskTypes.rosterDutyLineCreateNotifyPlannerRejected',
  EmployeeDutylineCreatedTask:
    'plannerMessages.taskTypes.employeeDutylineCreatedTask',
  FlexPortalMatchNotifyPlanner:
    'plannerMessages.taskTypes.flexPortalMatchNotifyPlanner',
  FlexPortalMatchNotifyPlannersRejectTask:
    'plannerMessages.taskTypes.flexPortalMatchNotifyPlannersRejectTask',
  DutyLineUnlinkNotificationNotifyPlanner:
    'plannerMessages.taskTypes.dutyLineUnlinkNotificationNotifyPlanner'
};

const getTaskTypeLabel = (message: PlannerMessage, t?: TranslateFn): string => {
  const translationKey = TASK_TYPE_TRANSLATION_KEYS[message.taskDefinitionKey];
  const fallback = message.taskName ?? message.taskDefinitionKey;

  if (!translationKey || !t) {
    return fallback;
  }

  return t(translationKey, { defaultValue: fallback });
};

const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, '').trim();

const getTaskVariable = (
  message: PlannerMessage,
  name: string
): string | undefined =>
  message.taskVariables?.find((v) => v.name === name)?.value;

const extractFromBodyTable = (
  body: string,
  label: string
): string | undefined => {
  const regex = new RegExp(
    `<td>\\s*${label}:?\\s*</td>\\s*<td>([^<]*)</td>`,
    'i'
  );
  const match = body.match(regex);
  return match?.[1]?.trim();
};

const extractHoursFromSection = (
  body: string,
  sectionTitle: string
): number | null => {
  const sectionRegex = new RegExp(
    `<h3>\\s*${sectionTitle}\\s*</h3>([\\s\\S]*?)(?=<h3>|<p>Met vriendelijke)`,
    'i'
  );
  const sectionMatch = body.match(sectionRegex);
  if (!sectionMatch) return null;
  const hoursMatch = sectionMatch[1].match(
    /<td>\s*Uren:?\s*<\/td>\s*<td>([^<]*)<\/td>/i
  );
  if (!hoursMatch) return null;
  const val = parseFloat(hoursMatch[1].trim().replace(',', '.'));
  return isNaN(val) ? null : val;
};

const extractHoursDiff = (
  body: string
): { hoursDiff: number | null; newHours: number | null } => {
  const newHours = extractHoursFromSection(body, 'Nieuwe dienst');
  const oldHours = extractHoursFromSection(body, 'Laatst goedgekeurde dienst');
  const hoursDiff =
    newHours !== null && oldHours !== null
      ? Math.round((newHours - oldHours) * 100) / 100
      : null;
  return { hoursDiff, newHours };
};

const extractFieldFromSection = (
  body: string,
  sectionTitle: string,
  fieldLabel: string
): string | null => {
  const sectionRegex = new RegExp(
    `<h3>\\s*${sectionTitle}\\s*</h3>([\\s\\S]*?)(?=<h3>|<p>Met vriendelijke)`,
    'i'
  );
  const sectionMatch = body.match(sectionRegex);
  if (!sectionMatch) return null;
  const fieldRegex = new RegExp(
    `<td>\\s*${fieldLabel}:?\\s*</td>\\s*<td>([^<]*)</td>`,
    'i'
  );
  const fieldMatch = sectionMatch[1].match(fieldRegex);
  return fieldMatch?.[1]?.trim() || null;
};

const extractDusFromSection = (
  body: string,
  sectionTitle: string
): string | null => {
  const sectionRegex = new RegExp(
    `<h3>\\s*${sectionTitle}\\s*</h3>([\\s\\S]*?)(?=<h3>|<p>Met vriendelijke)`,
    'i'
  );
  const sectionMatch = body.match(sectionRegex);
  if (!sectionMatch) return null;
  // Periodes cell can contain <br/> so match across tags
  const periodesRegex = /<td>\s*Periodes:?\s*<\/td>\s*<td>([\s\S]*?)<\/td>/i;
  const periodesMatch = sectionMatch[1].match(periodesRegex);
  if (!periodesMatch) return null;
  // Extract DUS names from each period line: "HH:mm - HH:mm DUSname"
  const lines = periodesMatch[1].split(/<br\s*\/?>/).map((l) => l.trim());
  const dusNames = lines
    .map((line) =>
      line.replace(/^\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}\s*/, '').trim()
    )
    .filter(Boolean);
  // Return unique DUS names joined
  return [...new Set(dusNames)].join(', ') || null;
};

const extractFieldChange = (
  body: string,
  fieldLabel: string
): string | undefined => {
  const newVal = extractFieldFromSection(body, 'Nieuwe dienst', fieldLabel);
  const oldVal = extractFieldFromSection(
    body,
    'Laatst goedgekeurde dienst',
    fieldLabel
  );
  if (!newVal && !oldVal) return undefined;
  if (!oldVal || newVal === oldVal) return newVal ?? undefined;
  return `${oldVal} → ${newVal}`;
};

const extractDusChange = (body: string): string | undefined => {
  const newDus = extractDusFromSection(body, 'Nieuwe dienst');
  const oldDus = extractDusFromSection(body, 'Laatst goedgekeurde dienst');
  if (!newDus && !oldDus) return undefined;
  if (newDus === oldDus || !oldDus) return undefined;
  return `${oldDus} → ${newDus}`;
};

const extractSubjectParts = (
  subject: string
): { title: string; subtitle: string } => {
  const titleMatch = subject.match(/<h3>([^<]*)<\/h3>/);
  const spanMatch = subject.match(/<span>([^<]*)<\/span>/);
  return {
    title: titleMatch?.[1]?.trim() ?? stripHtml(subject),
    subtitle: spanMatch?.[1]?.trim() ?? ''
  };
};

const stripLeadingZeros = (text: string): string =>
  text
    .replace(/(^|[\s(])0(\d[\s,])/g, '$1$2')
    .replace(/(^|[\s(])0(\d:\d{2})/g, '$1$2');

const compactDateRange = (dateRange: string): string => {
  if (!dateRange) return '';
  const parts = dateRange.split(' - ');
  if (parts.length !== 2) return stripLeadingZeros(dateRange);

  const startDatePart = stripLeadingZeros(
    parts[0].replace(/,\s*\d{2}:\d{2}$/, '').trim()
  );
  const endDatePart = stripLeadingZeros(
    parts[1].replace(/,\s*\d{2}:\d{2}$/, '').trim()
  );

  if (startDatePart === endDatePart) {
    return startDatePart;
  }
  return `${startDatePart} - ${endDatePart}`;
};

const parseLeaveRequest = (
  message: PlannerMessage,
  t?: TranslateFn
): ParsedMessageData => {
  const { subtitle } = extractSubjectParts(message.message?.subject ?? '');
  const body = message.message?.body ?? '';

  const parts = subtitle.split('|').map((s) => s.trim());

  // Standard format: "Employee | DateRange"
  // Some leave tasks (SeenTask, ProcessedTask) may not have pipe-separated parts
  const hasPipeParts = parts.length >= 2;

  const employeeName = hasPipeParts
    ? parts[0]
    : extractFromBodyTable(body, 'Medewerker') ?? '';
  const dateRange = hasPipeParts
    ? compactDateRange(parts[1])
    : compactDateRange(
        [
          extractFromBodyTable(body, 'Vanaf'),
          extractFromBodyTable(body, 'Tot en met')
        ]
          .filter(Boolean)
          .join(' - ')
      );

  const planUnit = extractFromBodyTable(body, 'Planeenheid') ?? '';
  const note = getTaskVariable(message, 'empNote') ?? '';
  const leaveType = extractFromBodyTable(body, 'Soort verlof') ?? '';

  return {
    employeeName,
    dateRange,
    taskTypeLabel: getTaskTypeLabel(message, t),
    planUnit,
    createdAt: formatCreatedAt(message.createTime),
    note: note === '-' ? '' : note,
    leaveType
  };
};

const parseFlexNotification = (
  message: PlannerMessage,
  t?: TranslateFn
): ParsedMessageData => {
  const { subtitle } = extractSubjectParts(message.message?.subject ?? '');
  const body = message.message?.body ?? '';

  // Flex subject variants:
  //   5 parts: "PlanUnit | Function | Date | TimeRange | Employee"
  //   4 parts (reject): "PlanUnit | Function | Date | TimeRange" (no employee)
  const parts = subtitle.split('|').map((s) => s.trim());
  const planUnit = parts[0] ?? '';
  const dateRange = parts[2] ? `${parts[2]} (${parts[3] ?? ''})`.trim() : '';
  const employeeName =
    parts.length >= 5
      ? parts[parts.length - 1]
      : extractFromBodyTable(body, 'Medewerker') ?? '';

  return {
    employeeName,
    dateRange: stripLeadingZeros(dateRange),
    taskTypeLabel: getTaskTypeLabel(message, t),
    planUnit,
    createdAt: formatCreatedAt(message.createTime),
    note: ''
  };
};

const parseDutyChange = (
  message: PlannerMessage,
  t?: TranslateFn
): ParsedMessageData => {
  const { subtitle } = extractSubjectParts(message.message?.subject ?? '');
  const body = message.message?.body ?? '';

  // Standard duty: "PlanUnit | Date | TimeRange | Employee" (4 parts)
  // EmployeeDutylineCreatedTask: "Date | TimeRange | PlanUnit" (3 parts, no employee)
  const parts = subtitle.split('|').map((s) => s.trim());

  let planUnit: string;
  let dateRange: string;
  let employeeName: string;

  if (
    message.taskDefinitionKey === 'EmployeeDutylineCreatedTask' ||
    parts.length === 3
  ) {
    dateRange = parts[0] ? `${parts[0]} (${parts[1] ?? ''})`.trim() : '';
    planUnit = parts[2] ?? '';
    employeeName = extractFromBodyTable(body, 'Medewerker') ?? '';
  } else {
    planUnit = parts[0] ?? '';
    dateRange = parts[1] ? `${parts[1]} (${parts[2] ?? ''})`.trim() : '';
    employeeName = parts.length >= 4 ? parts[parts.length - 1] : '';
  }

  const planUnitFromBody = extractFromBodyTable(body, 'Planeenheid');
  const note =
    extractFromBodyTable(body, 'Reden wijziging') ??
    extractFromBodyTable(body, 'Reden aanmaken') ??
    '';
  const { hoursDiff, newHours } = extractHoursDiff(body);
  const planUnitChange = extractFieldChange(body, 'Planeenheid');
  const dutyCodeChange = extractDusChange(body);

  return {
    employeeName,
    dateRange: stripLeadingZeros(dateRange),
    taskTypeLabel: getTaskTypeLabel(message, t),
    planUnit: planUnitChange ?? planUnitFromBody ?? planUnit,
    createdAt: formatCreatedAt(message.createTime),
    note,
    hoursDiff,
    newHours,
    planUnitChange,
    dutyCodeChange
  };
};

const parseMessageData = (
  message: PlannerMessage,
  t?: TranslateFn
): ParsedMessageData => {
  const key = message.taskDefinitionKey;

  if (
    LEAVE_REQUEST_TASK_KEYS.includes(
      key as (typeof LEAVE_REQUEST_TASK_KEYS)[number]
    )
  ) {
    return parseLeaveRequest(message, t);
  }

  if (
    FLEX_NOTIFICATION_TASK_KEYS.includes(
      key as (typeof FLEX_NOTIFICATION_TASK_KEYS)[number]
    )
  ) {
    return parseFlexNotification(message, t);
  }

  if (
    DUTY_CHANGE_TASK_KEYS.includes(
      key as (typeof DUTY_CHANGE_TASK_KEYS)[number]
    )
  ) {
    return parseDutyChange(message, t);
  }

  // Fallback: best-effort extraction
  const { subtitle } = extractSubjectParts(message.message?.subject ?? '');
  const parts = subtitle.split('|').map((s) => s.trim());

  return {
    employeeName: parts[0] ?? '',
    dateRange: parts[1] ?? '',
    taskTypeLabel: getTaskTypeLabel(message, t),
    planUnit: '',
    createdAt: formatCreatedAt(message.createTime),
    note: ''
  };
};

export default parseMessageData;
